import React, { useState, useEffect } from 'react';
import { Handshake, Store, Users, MessageCircle } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Handshake,
  Store,
  Users,
  MessageCircle
};

export const CountdownSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const eventDate = new Date(settings.eventDate).getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = eventDate - now;

      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [settings.eventDate]);

  const formatNumber = (num) => num.toString().padStart(2, '0');

  // Feature cards with translations
  const countdownFeatures = [
    {
      icon: 'Handshake',
      titleKey: 'countdownFeatures.b2b',
      descKey: 'countdownFeatures.b2bDesc'
    },
    {
      icon: 'Store',
      titleKey: 'countdownFeatures.marketplace',
      descKey: 'countdownFeatures.marketplaceDesc'
    },
    {
      icon: 'Users',
      titleKey: 'countdownFeatures.delegations',
      descKey: 'countdownFeatures.delegationsDesc'
    },
    {
      icon: 'MessageCircle',
      titleKey: 'countdownFeatures.networking',
      descKey: 'countdownFeatures.networkingDesc'
    }
  ];

  return (
    <section id="countdown" className="bg-gray-50 py-20">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          {getBilingualValue(settings.countdown?.title, 'countdown.title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-8">
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div 
              className="text-5xl font-bold mb-2"
              style={{ color: settings.colors?.primary || '#0E7A5F' }}
            >
              {formatNumber(timeLeft.days)}
            </div>
            <div className="text-gray-500">{t('countdown.days')}</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div 
              className="text-5xl font-bold mb-2"
              style={{ color: settings.colors?.primary || '#0E7A5F' }}
            >
              {formatNumber(timeLeft.hours)}
            </div>
            <div className="text-gray-500">{t('countdown.hours')}</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div 
              className="text-5xl font-bold mb-2"
              style={{ color: settings.colors?.primary || '#0E7A5F' }}
            >
              {formatNumber(timeLeft.minutes)}
            </div>
            <div className="text-gray-500">{t('countdown.minutes')}</div>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <div 
              className="text-5xl font-bold mb-2"
              style={{ color: settings.colors?.primary || '#0E7A5F' }}
            >
              {formatNumber(timeLeft.seconds)}
            </div>
            <div className="text-gray-500">{t('countdown.seconds')}</div>
          </div>
        </div>
        <p className="text-gray-600 mb-16">
          {getBilingualValue(settings.countdown?.subtitle, 'countdown.subtitle')}: {settings.eventDate ? new Date(settings.eventDate).toLocaleDateString() : '19 June 2026'}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {countdownFeatures.map((feature, idx) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div key={feature.titleKey} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                {IconComponent && (
                  <IconComponent 
                    size={32} 
                    className="mx-auto mb-4"
                    style={{ color: settings.colors?.primary || '#0E7A5F' }}
                  />
                )}
                <h3 className="font-bold text-lg mb-2">{t(feature.titleKey)}</h3>
                <p className="text-gray-600 text-sm">{t(feature.descKey)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
